import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { getStoredUserId, userCanEditBoard } from "features/analytics/studio/utils/boardAccess";

/**
 * Pick an existing board (user can edit) or create a new board before saving a card.
 * Renders as a fixed popover aligned under the Save button (anchorRef).
 */
export default function SaveToBoardModal({
  open,
  onClose,
  anchorRef,
  teamId,
  loadBoards,
  onSaveToExisting,
  onSaveToNewBoard,
  saving = false,
}) {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [boards, setBoards] = useState([]);
  const [hoverId, setHoverId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [menuPos, setMenuPos] = useState(null);

  const computePosition = useCallback(() => {
    const margin = 8;
    const width = 320;
    const anchor = anchorRef?.current;
    if (!anchor) {
      const top = 56 + margin;
      const maxPanelH = Math.min(480, window.innerHeight - top - margin);
      setMenuPos({ top, right: margin, width, maxPanelH });
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const top = rect.bottom + margin;
    const right = Math.max(margin, window.innerWidth - rect.right);
    const maxPanelH = Math.min(480, Math.max(200, window.innerHeight - top - margin));
    setMenuPos({ top, right, width, maxPanelH });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    computePosition();
    window.addEventListener("resize", computePosition);
    return () => window.removeEventListener("resize", computePosition);
  }, [open, computePosition]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setBoards([]);
      setHoverId(null);
      setSelectedId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await loadBoards({ teamId });
        const uid = getStoredUserId();
        const raw = Array.isArray(list) ? list : [];
        const editable = uid != null ? raw.filter((b) => b && userCanEditBoard(b, uid)) : [];
        if (!cancelled) setBoards(editable);
      } catch (e) {
        console.error("SaveToBoardModal: load boards failed", e);
        if (!cancelled) setBoards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, teamId, loadBoards]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter((b) => (b.name || "").toLowerCase().includes(q));
  }, [boards, search]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open || typeof document === "undefined") return null;

  const pos = menuPos || { top: 64, right: 16, width: 320, maxPanelH: 360 };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[199]"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.08)" }}
        aria-hidden
        onMouseDown={(e) => {
          if (!saving) onClose?.();
        }}
      />
      <div
        className="fixed z-[200] flex min-h-0 flex-col overflow-hidden rounded-xl border shadow-xl"
        style={{
          fontFamily: "Inter, sans-serif",
          backgroundColor: "var(--surface-base)",
          borderColor: theme.border || "var(--border-default)",
          top: pos.top,
          right: pos.right,
          width: pos.width,
          maxHeight: pos.maxPanelH,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-to-board-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="save-to-board-title" className="sr-only">
          Save to board
        </h2>
        <div className="p-3 border-b shrink-0" style={{ borderColor: theme.border || "var(--border-default)" }}>
          <div
            className="flex items-center gap-2 rounded-lg border px-3 py-2"
            style={{ borderColor: `${theme.primaryLight}55` }}
          >
            <Search className="h-4 w-4 shrink-0" style={{ color: theme.textMuted }} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Save to board…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
              style={{ color: theme.textPrimary }}
              disabled={saving}
              autoFocus
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-6 text-center text-sm" style={{ color: theme.textMuted }}>
              Loading boards…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm" style={{ color: theme.textMuted }}>
              {boards.length === 0
                ? "No boards you can edit yet."
                : "No boards match your search."}
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map((board) => {
                const id = String(board.id);
                const active = hoverId === id || selectedId === id;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      disabled={saving}
                      className="w-full px-4 py-2.5 text-left text-sm transition-colors truncate"
                      style={{
                        backgroundColor: active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                        color: active ? theme.primaryLight : theme.textPrimary,
                        fontWeight: active ? 600 : 400,
                      }}
                      onMouseEnter={() => setHoverId(id)}
                      onMouseLeave={() => setHoverId(null)}
                      onClick={() => {
                        setSelectedId(id);
                        onSaveToExisting?.(id);
                      }}
                    >
                      {board.name || "Untitled board"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className="border-t px-2 py-1 shrink-0"
          style={{
            borderColor: theme.border || "var(--border-default)",
            backgroundColor: theme.bgOverlay || "var(--surface-sunken)",
          }}
        >
          <button
            type="button"
            disabled={saving}
            className="w-full rounded-md py-1.5 text-center text-sm font-semibold transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.textSecondary;
            }}
            onClick={() => onSaveToNewBoard?.()}
          >
            {saving ? "Saving…" : "Save to New Board"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
